import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import Alert from '@components/Alert';
import { FormikDropdownField, FormikTextField } from '@components/FormikFields';
import { ListItemToggle } from '@components/ListItem';
import OrgFeatureToggleList from '@components/OrgFeatureToggleList';
import RoundedButton from '@components/RoundedButton/index';
import SkeletonLoader from '@components/SkeletonLoader';

import { useListFeatureCatalog } from 'generated/custom-plan.generated';

import WizardStepIndicator from './WizardStepIndicator';
import { CUSTOM_LICENSE_ID } from './license-options';
import type { LicenseFeatureValues } from './types';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  catalogError: {
    id: 'newAccount.featureCatalogError',
    defaultMessage: 'Could not load the feature list',
  },
  catalogErrorBody: {
    id: 'newAccount.featureCatalogError.body',
    defaultMessage:
      'Organization features cannot be selected until this loads. Your other entries have been kept.',
  },
  retry: {
    id: 'GLOBAL.retry',
    defaultMessage: 'Retry',
  },
  license: {
    id: 'newAccount.license',
    defaultMessage: 'Enter License and Plan information for this organization',
  },
  licenseLabel: {
    id: 'newAccount.licenseLabel',
    defaultMessage: 'License',
  },
  licenseCustom: {
    id: 'newAccount.licenseCustom',
    defaultMessage: 'Custom',
  },
  patientSeats: {
    id: 'newAccount.patientSeats',
    defaultMessage: 'Patient seats',
  },
  practitionerSeats: {
    id: 'newAccount.practitionerSeats',
    defaultMessage: 'Practitioner seats are unlimited.',
  },
  seatsInteger: {
    id: 'newAccount.patientSeats.integer',
    defaultMessage: 'Patient seats must be a whole number',
  },
  seatsMin: {
    id: 'newAccount.patientSeats.min',
    defaultMessage: 'Patient seats must be greater than 0',
  },
  patientMfaTitle: {
    id: 'organization.setting.mfa.patient.title',
    defaultMessage: 'Patient MFA',
  },
  patientMfaSubtitle: {
    id: 'organization.setting.mfa.patient.subtitle',
    defaultMessage: 'Require patients to set up MFA when logging in.',
  },
  practitionerMfaTitle: {
    id: 'organization.setting.mfa.practitioner.title',
    defaultMessage: 'Practitioner User MFA',
  },
  practitionerMfaSubtitle: {
    id: 'organization.setting.mfa.practitioner.subtitle',
    defaultMessage:
      'Require practitioners and org admins to set up MFA when logging in.',
  },
  medlyTitle: {
    id: 'organization.setting.licensePlan.medlyTitle',
    defaultMessage: 'Enable Medly for organization',
  },
  medlySubtitle: {
    id: 'organization.setting.licensePlan.medlySubtitle',
    defaultMessage: 'Include Medly as an add-on',
  },
});

/** The design sits the licence and seat fields side by side at a fixed width. */
const FIELD_WIDTH = 280;
const BUTTON_HEIGHT = 44;

type FormValues = LicenseFeatureValues;

interface LicenseInformationProps {
  initialValues: FormValues;
  onSubmit?: (values: FormValues) => void;
  onBack?: () => void;
}

function LicenseInformationForm({
  initialValues,
  onSubmit,
  onBack,
}: LicenseInformationProps) {
  const {
    data: catalogData,
    error: catalogError,
    loading: catalogLoading,
    refetch: refetchCatalog,
  } = useListFeatureCatalog();
  const { formatMessage } = useIntl();

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    license_id: Yup.string().required(formatMessage(messages.requiredText)),
    limit_patients: Yup.number()
      .typeError(formatMessage(messages.seatsInteger))
      .required(formatMessage(messages.requiredText))
      .integer(formatMessage(messages.seatsInteger))
      .min(1, formatMessage(messages.seatsMin)),
    org_feature_ids: Yup.array().of(Yup.string().required()).default([]),
    is_patient_mfa_required: Yup.boolean(),
    is_practitioner_mfa_required: Yup.boolean(),
    medly_enabled: Yup.boolean(),
  });

  async function handleSubmit(values: FormValues) {
    // Formik parks '' in the field when the admin clears it (parseFloat of an
    // empty box is NaN), so coerce before the value leaves the form.
    onSubmit?.({ ...values, limit_patients: Number(values.limit_patients) });
  }

  // The first iteration of the epic offers one licence: "Custom". It is built
  // here rather than read from listLicenses because it is the absence of a
  // shared tier, so there is no tier row behind it. Shared tiers rejoin this
  // list in a later iteration.
  const licenseOptions = [
    { k: CUSTOM_LICENSE_ID, v: formatMessage(messages.licenseCustom) },
  ];

  const features = catalogData?.listFeatureCatalog ?? [];

  // An empty list is indistinguishable from "this org has no features", so a
  // failed fetch must not be committable as an empty `org_feature_ids`. The
  // wrapped hook reports through the shared handler, which is a boundary rather
  // than a snackbar — and a network error is dropped there entirely — so the
  // failure is stated inline instead, keeping the admin's entered values.
  const catalogFailed = Boolean(catalogError);

  return (
    <SkeletonLoader loading={catalogLoading}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          /** Add or remove a feature from the org's entitlements. */
          function handleToggleFeature(featureId: string, enabled: boolean) {
            const next = enabled
              ? [...values.org_feature_ids, featureId]
              : values.org_feature_ids.filter((id) => id !== featureId);
            setFieldValue('org_feature_ids', next);
          }

          return (
            <Form>
              <Stack gap={2.25}>
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  flexWrap="wrap"
                  gap={2.5}
                >
                  <Box width={FIELD_WIDTH}>
                    <FormikDropdownField
                      label={formatMessage(messages.licenseLabel)}
                      name="license_id"
                      options={licenseOptions}
                      required
                    />
                  </Box>
                  <Box width={FIELD_WIDTH}>
                    <FormikTextField
                      fullWidth
                      helperText={formatMessage(messages.practitionerSeats)}
                      label={formatMessage(messages.patientSeats)}
                      name="limit_patients"
                      required
                      type="number"
                    />
                  </Box>
                </Stack>

                {catalogFailed && (
                  <Alert
                    action={
                      <RoundedButton
                        autoSize
                        height={32}
                        margin={0}
                        onClick={() => refetchCatalog()}
                        variant="white"
                      >
                        {formatMessage(messages.retry)}
                      </RoundedButton>
                    }
                    data-testid="feature-catalog-error"
                    title={formatMessage(messages.catalogError)}
                  >
                    {formatMessage(messages.catalogErrorBody)}
                  </Alert>
                )}

                <OrgFeatureToggleList
                  disabled={isSubmitting}
                  enabledIds={values.org_feature_ids}
                  features={features}
                  onToggle={handleToggleFeature}
                />

                {/* Legacy account settings: the feature toggles above are UI
                    only for now and do not drive these on the backend. */}
                <Box>
                  <ListItemToggle
                    disabled={isSubmitting}
                    enabled={values.is_practitioner_mfa_required}
                    icon_name="user-shield"
                    onToggle={(checked) =>
                      setFieldValue('is_practitioner_mfa_required', checked)
                    }
                    primary_text={formatMessage(messages.practitionerMfaTitle)}
                    secondary_text={formatMessage(
                      messages.practitionerMfaSubtitle
                    )}
                  />
                  <ListItemToggle
                    disabled={isSubmitting}
                    enabled={values.is_patient_mfa_required}
                    icon_name="user-shield"
                    onToggle={(checked) =>
                      setFieldValue('is_patient_mfa_required', checked)
                    }
                    primary_text={formatMessage(messages.patientMfaTitle)}
                    secondary_text={formatMessage(messages.patientMfaSubtitle)}
                  />
                  <ListItemToggle
                    disabled={isSubmitting}
                    enabled={values.medly_enabled}
                    icon_name="heart"
                    onToggle={(checked) =>
                      setFieldValue('medly_enabled', checked)
                    }
                    primary_text={formatMessage(messages.medlyTitle)}
                    secondary_text={formatMessage(messages.medlySubtitle)}
                  />
                </Box>

                <Stack gap={1.5}>
                  <RoundedButton
                    disabled={isSubmitting || catalogFailed}
                    height={BUTTON_HEIGHT}
                    margin={0}
                    type="submit"
                    width="100%"
                  >
                    {formatMessage(messages.submit)}
                  </RoundedButton>
                  {onBack && (
                    <RoundedButton
                      disabled={isSubmitting}
                      height={BUTTON_HEIGHT}
                      margin={0}
                      onClick={onBack}
                      type="button"
                      width="100%"
                    >
                      {formatMessage(messages.back)}
                    </RoundedButton>
                  )}
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </SkeletonLoader>
  );
}

export default function LicenseInformation({
  initialValues,
  onSubmit,
  onBack,
}: LicenseInformationProps) {
  const { formatMessage } = useIntl();
  return (
    <Card>
      <CardHeader
        action={<WizardStepIndicator currentStep={0} />}
        title={formatMessage(messages.license)}
        titleTypographyProps={{
          variant: 'h6',
          color: 'textPrimary',
          component: 'h2',
        }}
      />
      <Divider />
      <CardContent>
        <Stack gap={1}>
          <LicenseInformationForm
            initialValues={initialValues}
            onBack={onBack}
            onSubmit={onSubmit}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
